'use client';
/**
 * LE PARCOURS D'ACCUEIL D'UN NOUVEL ÉQUIPEMENT (Ines, 28/10/2026) — réduit au BANDEAU D'AVANCEMENT de l'onglet
 * « Présentation » (spec 002, récit 9, 04/11/2026).
 *
 * Quatre pans — achat, équipement, personnel, réactifs & examens — qui regardent la fiche et disent ce qui est déjà
 * relié et ce qui reste à faire. Le parcours n'est plus une liste qui renvoie ailleurs : il est l'ORDRE DES BLOCS de
 * la Présentation, et chaque point manquant du bandeau mène à son bloc, dans la même page. Il COMPLÈTE le dossier de mise en
 * service (les douze étapes à cocher, l'autorisation du RQ) sans le remplacer, et ne bloque rien : seul le dossier
 * interdit l'usage. Tout se dérive — rien n'est stocké ici. Le calcul est dans `lib/accueil-equipement.ts`.
 *
 * Le circuit analytique est celui confirmé : réactif (lot, produit du catalogue) → équipement par défaut ;
 * équipement → technique → examen. Les trois lectures qui ne sont pas dans le magasin (techniques, catalogue,
 * habilitations) se font ici, et tant qu'elles ne sont pas revenues le point est « pas encore lu », jamais « à faire ».
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useGed } from '@/lib/store';
import { card, Pastille } from '@/components/ui';
import { lbl } from './commun';
import { Bouton } from '@/shared/Bouton';
import type { GedEquipement } from '@/lib/types';
import { rubriquesManquantes } from '@/lib/rubriques-equipement';
import {
  parcoursAccueil, bilanPan, panSuivant,
  type Pan, type EtatPoint, type EntreeAccueil, type BlocPresentation,
} from '@/lib/accueil-equipement';

type Lu<T> = T | null;

/** Une lecture GET qui rend `null` tant qu'elle n'est pas revenue — et aussi si elle échoue : dans les deux cas
 *  on ne sait pas, et « à faire » serait une affirmation. */
function useLecture<T>(url: string | null, extraire: (j: unknown) => T): Lu<T> {
  const [etat, setEtat] = useState<{ url: string; valeur: T } | null>(null);
  // `extraire` est recréé à chaque rendu : on ne relit pas quand seule sa référence change.
  const extraireRef = React.useRef(extraire);
  React.useLayoutEffect(() => { extraireRef.current = extraire; });
  useEffect(() => {
    if (!url) return;
    let vivant = true;
    void fetch(url, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant && j) setEtat({ url, valeur: extraireRef.current(j) }); })
      .catch(() => {});
    return () => { vivant = false; };
  }, [url]);
  return url && etat?.url === url ? etat.valeur : null;
}

export const ETAT_POINT: Record<EtatPoint, { ton: 'success' | 'warning' | 'muted' | 'neutral'; libelle: string }> = {
  fait: { ton: 'success', libelle: 'Fait' },
  afaire: { ton: 'warning', libelle: 'À faire' },
  sans_objet: { ton: 'muted', libelle: 'Sans objet' },
  inconnu: { ton: 'neutral', libelle: 'Pas encore lu' },
};

/** Ce que le parcours lit et calcule pour un appareil. `version` : à augmenter pour relire ce qui vient du serveur
 *  (habilitations, catalogue) après une saisie faite dans la Présentation. */
export function usePansAccueil(e: GedEquipement, version = 0): {
  pans: Pan[];
  produits: { code: string; libelle: string; nbExamens: number }[] | null;
  techniques: { code: string; nom: string; nbExamens: number | null }[] | null;
} {
  const { lots, cartesCqi, maintenances } = useGed();
  const code = e.code || '';
  const id = encodeURIComponent(e.documentId);

  const techniques = useLecture(
    e.documentId
      ? `/api/ged/techniques?filters[equipements][documentId][$eq]=${id}&filters[actif][$eq]=true&fields[0]=code&fields[1]=nom&populate[fiches][fields][0]=code&pagination[pageSize]=100`
      : null,
    (j) => ((j as { data?: { code: string; nom: string; fiches?: unknown[] }[] }).data || [])
      .map((t) => ({ code: t.code, nom: t.nom, nbExamens: Array.isArray(t.fiches) ? t.fiches.length : null })),
  );
  const produits = useLecture(
    `/api/ged/ged/produits${version ? `?v=${version}` : ''}`,
    (j) => ((j as { lignes?: { code: string; libelle: string; equipementCode: string; actif: boolean; examens?: string[] }[] }).lignes || [])
      .filter((p) => p.actif && code && p.equipementCode === code)
      .map((p) => ({ code: p.code, libelle: p.libelle, nbExamens: (p.examens || []).length })),
  );
  const habilitations = useLecture(
    e.documentId ? `/api/ged/ged/equip/${id}/habilitations${version ? `?v=${version}` : ''}` : null,
    (j) => {
      const h = j as { habilitations?: { actif: boolean }[]; habilites?: Record<string, { orgId: string }[]> };
      const personnes = new Set(Object.values(h.habilites || {}).flat().map((p) => p.orgId));
      return { nbHabilites: personnes.size, nbHabilitations: (h.habilitations || []).filter((x) => x.actif).length };
    },
  );

  const pans = useMemo(() => {
    const ligneParc = (maintenances?.parc || []).find((p) => p.code === code);
    const entree: EntreeAccueil = {
      nature: e.nature, categorie: e.categorie, code,
      fournisseur: e.fournisseur, fournisseurCode: e.fournisseurCode,
      contrat: e.contrat, contactTechnique: e.contactTechnique, contacts: e.contacts,
      dateReception: e.dateReception, garantieJusquA: e.garantieJusquA, documentation: e.documentation,
      responsableNom: e.responsableNom, suppleantNom: e.suppleantNom,
      dossierMesOuvert: !!e.miseEnService?.etapes?.length,
      rubriquesManquantes: rubriquesManquantes(e as unknown as Record<string, unknown>).map((r) => r.label),
      techniques, produits,
      nbPlansMaintenance: ligneParc ? ligneParc.nbPlans : null,
      nbLots: (lots || []).filter((l) => code && l.equipementCode === code && l.statut !== 'retire').length,
      nbCartes: (cartesCqi || []).filter((c) => c.equipement === code).length,
      nbHabilites: habilitations ? habilitations.nbHabilites : null,
      nbHabilitations: habilitations ? habilitations.nbHabilitations : null,
    };
    return parcoursAccueil(entree);
  }, [e, code, techniques, produits, habilitations, lots, cartesCqi, maintenances]);

  return { pans, produits, techniques };
}

/** LE BANDEAU D'AVANCEMENT : « n/15 », et les points manquants, chacun menant à son bloc. */
export function BandeauAvancement({ pans, onBloc }: { pans: Pan[]; onBloc: (bloc: BlocPresentation) => void }) {
  const suivant = panSuivant(pans);
  const faits = pans.reduce((s, p) => s + bilanPan(p).faits, 0);
  const total = pans.reduce((s, p) => s + bilanPan(p).total, 0);
  const inconnus = pans.reduce((s, p) => s + bilanPan(p).inconnus, 0);
  const manquants = pans.flatMap((p) => p.points).filter((x) => x.etat === 'afaire' && x.cible);

  return (
    <section aria-label="Avancement du parcours d’accueil" style={{ ...card, padding: 12, display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ ...lbl, marginBottom: 0 }}>Parcours d’accueil</span>
        <Pastille ton={suivant ? 'warning' : 'success'} title="Points faits sur points applicables à cet appareil">
          {faits}/{total} points reliés
        </Pastille>
        {inconnus > 0 && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{inconnus} en cours de lecture</span>}
        {!suivant && !inconnus && (
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Tout ce qui se lit sur la fiche est relié. L’autorisation de mise en service reste au RQ.
          </span>
        )}
      </div>
      {manquants.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>À faire :</span>
          {manquants.map((pt) => (
            <Bouton key={pt.cle} taille="sm" variante="discret" title={pt.detail} onClick={() => onBloc(pt.cible!.bloc)}>
              {pt.libelle}
            </Bouton>
          ))}
        </div>
      )}
    </section>
  );
}
